import { RESUME_TTL_MS } from "./constants";

/**
 * Remembers each multipart upload in progress, so an upload interrupted by a
 * refresh, a closed tab or a crash can continue from the parts S3 already holds
 * when the user selects the same file again.
 *
 * Kept in localStorage, not sessionStorage, so it survives a closed tab. A
 * record is keyed by the file (name, size, last modified) and the resource it
 * uploads to. It holds the row the upload feeds (`assetId`), so the caller can
 * reuse that row rather than create a second one. A record expires 24 h after
 * the upload last signed a part, which is when the backend forgets the session.
 */

export type UploadResource = "media" | "spatial-dataset" | "field-import";

/** The parts of a File that identify it across page loads. */
export interface FileFingerprint {
  name: string;
  size: number;
  lastModified?: number;
}

export interface ResumeRecord {
  resource: UploadResource;
  uploadId: string;
  key: string;
  /** Media or spatial dataset id the upload feeds; none for field imports. */
  assetId?: number;
  fileName: string;
  fileSize: number;
  fileLastModified?: number;
  savedAt: number;
}

const PREFIX = "culturemap:upload-resume:";

const storageKey = (resource: UploadResource, file: FileFingerprint) =>
  `${PREFIX}${resource}|${file.name}|${file.size}|${file.lastModified ?? 0}`;

// Storage can be missing or throw (private windows, blocked site data); resuming
// is then unavailable, but uploading still works.
const read = (key: string): ResumeRecord | null => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const record = JSON.parse(raw) as ResumeRecord;
    if (!record?.uploadId || Date.now() - record.savedAt > RESUME_TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    return record;
  } catch {
    return null;
  }
};

const write = (key: string, record: ResumeRecord) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(record));
  } catch {
    // Not resumable after a reload; nothing else depends on it.
  }
};

export const resumeStore = {
  get(resource: UploadResource, file: FileFingerprint): ResumeRecord | null {
    return read(storageKey(resource, file));
  },

  set(resource: UploadResource, file: FileFingerprint, record: Pick<ResumeRecord, "uploadId" | "key" | "assetId">) {
    write(storageKey(resource, file), {
      ...record,
      resource,
      fileName: file.name,
      fileSize: file.size,
      fileLastModified: file.lastModified,
      savedAt: Date.now()
    });
  },

  /** Restarts the expiry: the backend session slides on every part it signs. */
  touch(resource: UploadResource, file: FileFingerprint) {
    const record = read(storageKey(resource, file));
    if (record) write(storageKey(resource, file), { ...record, savedAt: Date.now() });
  },

  clear(resource: UploadResource, file: FileFingerprint) {
    try {
      window.localStorage.removeItem(storageKey(resource, file));
    } catch {
      // See `read`.
    }
  },

  /** The file a record was made for, to `get` or `clear` it by. */
  fingerprintOf(record: ResumeRecord): FileFingerprint {
    return { name: record.fileName, size: record.fileSize, lastModified: record.fileLastModified };
  },

  /** Uploads that can still be resumed, for an "upload interrupted" notice. */
  list(resource?: UploadResource): ResumeRecord[] {
    try {
      return Object.keys(window.localStorage)
        .filter((key) => key.startsWith(PREFIX))
        .map(read)
        .filter((record): record is ResumeRecord => !!record && (!resource || record.resource === resource));
    } catch {
      return [];
    }
  }
};
